"use client";
import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { magneticHover } from "./magneticHover";
import { useRouteTransition } from "./RouteTransition";

export default function FluidMenu() {
  const router = useRouter();
  const { navigate } = useRouteTransition();
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const animation = useRef<gsap.core.Timeline>();
  const afterClose = useRef<(() => void) | undefined>();
  const overflow = useRef("");
  const isOpen = useRef(false);
  const [open, setOpen] = useState(false);
  const [navbarGone, setNavbarGone] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointer = window.matchMedia("(pointer: fine)");
    let cleanups: (() => void)[] = [];
    const sync = () => {
      cleanups.forEach((cleanup) => cleanup());
      cleanups = [];
      if (reduced.matches || !pointer.matches) return;
      const controls = [
        opener.current,
        dialog.current?.querySelector<HTMLButtonElement>(".fluid-close"),
      ];
      controls.forEach((control) => {
        const face = control?.querySelector<HTMLElement>(".fluid-control-face");
        const icon = control?.querySelector<HTMLElement>(".fluid-icon");
        const fill = control?.querySelector<HTMLElement>(".fluid-control-fill");
        if (!control || !face || !icon || !fill) return;
        const motion = {
          duration: 0.4,
          returnDuration: 0.9,
          returnEase: "elastic.out(1,0.3)",
        };
        cleanups.push(magneticHover(control, face, 0.5, 26, 26, motion));
        cleanups.push(magneticHover(control, icon, 0.8, 20, 20, motion));
        const enter = () =>
          gsap.to(fill, {
            yPercent: -100,
            duration: 0.45,
            ease: "power3.inOut",
            overwrite: true,
          });
        const leave = () =>
          gsap.to(fill, {
            yPercent: 0,
            duration: 0.35,
            ease: "power3.inOut",
            overwrite: true,
          });
        control.addEventListener("pointerenter", enter);
        control.addEventListener("pointerleave", leave);
        control.addEventListener("focus", enter);
        control.addEventListener("blur", leave);
        cleanups.push(() => {
          control.removeEventListener("pointerenter", enter);
          control.removeEventListener("pointerleave", leave);
          control.removeEventListener("focus", enter);
          control.removeEventListener("blur", leave);
          gsap.killTweensOf(fill);
          gsap.set(fill, { clearProps: "transform" });
        });
      });
      dialog.current
        ?.querySelectorAll<HTMLElement>(".fluid-link")
        .forEach((link) => {
          const content = link.querySelector<HTMLElement>(
            ".fluid-link-content"
          );
          if (content)
            cleanups.push(
              magneticHover(link, content, 0.35, 30, 16, {
                duration: 0.4,
                returnDuration: 0.9,
                returnEase: "elastic.out(1,0.3)",
              })
            );
        });
    };
    sync();
    reduced.addEventListener("change", sync);
    pointer.addEventListener("change", sync);
    return () => {
      cleanups.forEach((cleanup) => cleanup());
      reduced.removeEventListener("change", sync);
      pointer.removeEventListener("change", sync);
    };
  }, []);

  useEffect(() => {
    const header = opener.current
      ?.closest(".portfolio")
      ?.querySelector(".site-header");
    if (!header) return;
    const mobile = window.matchMedia("(max-width: 700px)");
    let headerVisible = true;
    const update = () => setNavbarGone(mobile.matches || !headerVisible);
    const observer = new IntersectionObserver(([entry]) => {
      headerVisible = entry.isIntersecting;
      update();
    });
    observer.observe(header);
    mobile.addEventListener("change", update);
    update();
    return () => {
      observer.disconnect();
      mobile.removeEventListener("change", update);
    };
  }, []);

  const finishClose = () => {
    dialog.current?.close();
    isOpen.current = false;
    document.documentElement.style.overflow = overflow.current;
    setOpen(false);
    opener.current?.focus({ preventScroll: true });
    const navigate = afterClose.current;
    afterClose.current = undefined;
    navigate?.();
  };
  const close = (after?: () => void) => {
    if (!dialog.current?.open) return;
    afterClose.current = after;
    setOpen(false);
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !animation.current
    ) {
      finishClose();
      return;
    }
    // Reverse the same geometry, including the expanding curved edge and icon.
    animation.current.reverse();
  };
  const show = () => {
    const element = dialog.current;
    if (!element) return;
    if (element.open) {
      afterClose.current = undefined;
      setOpen(true);
      animation.current?.play();
      return;
    }
    animation.current?.kill();
    overflow.current = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    element.setAttribute("autofocus", "");
    element.showModal();
    isOpen.current = true;
    setOpen(true);
    element
      .querySelector<HTMLButtonElement>(".fluid-close")
      ?.focus({ preventScroll: true });

    const panel = element.querySelector(".fluid-drawer");
    const edge = element.querySelector<SVGSVGElement>(".fluid-edge")!;
    const shade = element.querySelector(".fluid-shade");
    const links = element.querySelectorAll(".fluid-link");
    const firstLine = element.querySelector(
      ".fluid-close .fluid-icon-line:first-child"
    );
    const secondLine = element.querySelector(
      ".fluid-close .fluid-icon-line:last-child"
    );
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(panel, { x: 0, xPercent: 0 });
      gsap.set(edge, { scaleX: 0 });
      gsap.set(shade, { opacity: 1 });
      gsap.set(
        [
          links,
          element.querySelector(".fluid-menu-main h2"),
          element.querySelector(".fluid-socials"),
        ],
        { x: 0 }
      );
      gsap.set(firstLine, { y: 0, rotation: 45 });
      gsap.set(secondLine, { y: 0, rotation: -45 });
      return;
    }

    gsap.registerPlugin(CustomEase);
    const glide = CustomEase.create("portfolio-drawer", "0.7,0,0.2,1");
    animation.current = gsap
      .timeline({ paused: true, onReverseComplete: finishClose })
      .fromTo(
        panel,
        { xPercent: 100, x: edge.clientWidth },
        { xPercent: 0, x: 0, duration: 0.8, ease: glide },
        0
      )
      .fromTo(
        edge,
        { scaleX: 1 },
        { scaleX: 0, duration: 0.85, ease: glide },
        0
      )
      .fromTo(
        shade,
        { opacity: 0 },
        { opacity: 1, duration: 0.65, ease: glide },
        0
      )
      .fromTo(
        links,
        { x: Math.min(window.innerWidth * 0.15, 220), y: 0 },
        { x: 0, duration: 0.72, stagger: 0.03, ease: glide },
        0.04
      )
      .fromTo(
        element.querySelector(".fluid-menu-main h2"),
        { x: 55 },
        { x: 0, duration: 0.8, ease: glide },
        0
      )
      .fromTo(
        element.querySelector(".fluid-socials"),
        { x: 75, y: 0 },
        { x: 0, duration: 0.8, ease: glide },
        0.04
      )
      .fromTo(
        firstLine,
        { y: -3.5, rotation: 0 },
        { y: 0, rotation: 45, duration: 0.45, ease: glide },
        0
      )
      .fromTo(
        secondLine,
        { y: 3.5, rotation: 0 },
        { y: 0, rotation: -45, duration: 0.45, ease: glide },
        0
      );
    animation.current.play();
  };
  useEffect(
    () => () => {
      animation.current?.kill();
      if (isOpen.current)
        document.documentElement.style.overflow = overflow.current;
    },
    []
  );

  return (
    <>
      <button
        ref={opener}
        className={`fluid-toggle ${navbarGone || open ? "is-visible" : ""}`}
        onClick={show}
        aria-label="Open navigation"
        aria-expanded={open}
        aria-controls="fluid-navigation"
        aria-hidden={!navbarGone && !open}
        tabIndex={navbarGone || open ? 0 : -1}
      >
        <span className="fluid-control-face" aria-hidden="true">
          <span className="fluid-control-fill" />
          <span className="fluid-icon">
            <span className="fluid-icon-line" />
            <span className="fluid-icon-line" />
          </span>
        </span>
      </button>
      <dialog
        ref={dialog}
        id="fluid-navigation"
        className="fluid-menu"
        aria-labelledby="fluid-menu-title"
        tabIndex={-1}
        data-lenis-prevent
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const controls = event.currentTarget.querySelectorAll<HTMLElement>(
            'a[href], button:not([tabindex="-1"])'
          );
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus({ preventScroll: true });
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus({ preventScroll: true });
          }
        }}
      >
        <button
          className="fluid-shade"
          tabIndex={-1}
          onClick={() => close()}
          aria-label="Close navigation backdrop"
        />
        <div className="fluid-drawer">
          <svg
            className="fluid-edge"
            viewBox="0 0 180 1000"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M 180 0 C -60 0 -60 1000 180 1000 Z" />
          </svg>
          <div className="fluid-menu-main">
            <h2 id="fluid-menu-title">NAVIGATION</h2>
            <nav aria-label="Expanded navigation">
              {[
                ["Home", "home"],
                ["Work", "work"],
                ["About", "about"],
                ["Experience", "experience"],
                ["Contact", "contact"],
              ].map(([name, id]) => (
                <div className="fluid-link-mask" key={id}>
                  <a
                    className="fluid-link"
                    href={`/#${id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      close(() => router.push(`/#${id}`));
                    }}
                  >
                    <span className="fluid-link-content">
                      <span className="fluid-link-dot" aria-hidden="true" />
                      {name}
                    </span>
                  </a>
                </div>
              ))}
              <div className="fluid-link-mask">
                <a
                  className="fluid-link"
                  href="/history"
                  onClick={(event) => {
                    event.preventDefault();
                    const rect = event.currentTarget.getBoundingClientRect();
                    const x = event.detail
                      ? event.clientX
                      : rect.left + rect.width / 2;
                    const y = event.detail
                      ? event.clientY
                      : rect.top + rect.height / 2;
                    close(() => navigate("/history", x, y));
                  }}
                >
                  <span className="fluid-link-content">
                    <span className="fluid-link-dot" aria-hidden="true" />
                    History
                  </span>
                </a>
              </div>
            </nav>
          </div>
          <div className="fluid-socials">
            <span>ELSEWHERE</span>
            <div>
              <a
                href="https://github.com/SahiDemon"
                target="_blank"
                rel="noreferrer"
              >
                GitHub ↗
              </a>
              <a
                href="https://www.linkedin.com/in/sahindu-gayanuka-36926517b/"
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn ↗
              </a>
              <a href="mailto:contact@sahiya.lk">Email ↗</a>
            </div>
          </div>
        </div>
        {/* This control stays at the opener's viewport coordinates, outside the moving drawer. */}
        <button
          className="fluid-close"
          aria-label={open ? "Close navigation" : "Open navigation"}
          onClick={() => (open ? close() : show())}
        >
          <span className="fluid-control-face" aria-hidden="true">
            <span className="fluid-control-fill" />
            <span className="fluid-icon">
              <span className="fluid-icon-line" />
              <span className="fluid-icon-line" />
            </span>
          </span>
        </button>
      </dialog>
    </>
  );
}
